import { EditorService } from "./editor-service";
import { VoiceOverDto, VideoDto } from "@repo/api/editor";
import { BackgroundMusicDto } from "@repo/api/background-music";
import { FontDto } from "@repo/api/font";
import { AnimatedImageDto } from "@repo/api/animated-image";
import { SourceImageDto } from "@repo/api/source-image";
import { AnimatedImageProviderType } from "@repo/api/animated-image";
import { parseAnimatedImagePromptType } from "@repo/api/utils/prompt-type";
import { styleConfigs } from "@repo/api/animated-image-prompt-to-source-image-prompt";
import { overlayTabs } from "@/features/Editor/OverlaysTab/OverlaysTab";
import { SceneTypeDto } from "@repo/api/overlays";
import { getElementsByAspectRatio } from "@repo/api/utils/composition";
import { editorPanelTabs } from "@/features/Editor/TabNavigation/TabNavigation";
import { ThirdPartyEditorInfoDto } from "@repo/api/third-party-video";
import { VoiceActorDto } from "@repo/api/voice-actor";
import { AssetMediaAsyncDto } from "@repo/api/asset-media";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";
import { CreativeElementDto } from "@repo/api/creative-element";
import { CreativeProjectSheetState } from "./creative-project-state";

export interface TimelineItemToAnimate {
  promptType: string | null;
  motionIntensity?: number;
  endImageExternalId?: string | null;
}

export interface VoiceOverForm {
  script: string;
  voiceActorExternalId: string | null;
}

export interface TimelineItemAnimationForm {
  items: Record<
    CreativeProjectTimelineItem["externalId"],
    TimelineItemToAnimate
  >;
  provider: AnimatedImageProviderType | null;
}

export interface CropperState {
  timelineItemExternalId: string;
  animatedImageExternalId: string;
}

export interface CreditDialogState {
  isOpen: boolean;
  openedFromAnimate: boolean;
}

export interface SourceImageSelectorDialogState {
  isOpen: boolean;
}

export interface QuickAnimationsDialogState {
  openTimelineItemExternalId: string | null;
}

export interface MediaSelectorDialogState {
  isOpen: boolean;
}

export type BackgroundMusicView = "LIST" | "FORM" | "HIDDEN";

export interface BackgroundMusicDialogState {
  view: BackgroundMusicView;
  editingTrackExternalId: string | undefined;
}

export type FontDialogView = "LIST" | "FORM" | "HIDDEN";

export interface FontDialogState {
  view: FontDialogView;
}

export interface OverlaysTabState {
  currentTab: (typeof overlayTabs)[number]["value"];
  currentElement: {
    externalId: string;
    sceneType: SceneTypeDto;
  } | null;
  mediaSelectorDialogState: MediaSelectorDialogState;
}

export type VoiceoverView =
  "CREATE_VOICEOVER" | "CAPTIONS" | "LIST" | "SCRIPT_WRITER";

export interface VoiceoverTabState {
  view: VoiceoverView;
  scriptWriterInput: string;
}

// Do not add current frame here. It will update too often and cause unnecessary re-renders.
export interface VideoPreviewState {
  isPlaying: boolean;
  isFullscreen: boolean;
  isMuted: boolean;
}

export interface EditorPanelState {
  currentTab: (typeof editorPanelTabs)[number]["value"];
}

export type EditorState = {
  isInitialized: boolean;

  // Backend Data
  teamExternalId: string;
  videoExternalId: string;
  video: VideoDto | null;
  sourceImages: Record<string, SourceImageDto>;
  animatedImages: Record<string, AnimatedImageDto>;
  voiceOvers: VoiceOverDto[];
  backgroundMusics: Record<string, BackgroundMusicDto>;
  voiceActors: VoiceActorDto[];
  compositionFonts: FontDto[];
  assetMedias: Record<string, AssetMediaAsyncDto>;
  creativeElements: Record<string, CreativeElementDto>;

  // UI State
  isEditingVideoName: boolean;
  thirdPartyEditorInfo: ThirdPartyEditorInfoDto | null;
  previewVoiceOver: VoiceOverDto | null;
  isPreviewVoiceOverPlaying: boolean;
  voiceOverForm: VoiceOverForm;
  animationForm: TimelineItemAnimationForm;
  cropperState: CropperState | null;
  creditDialogState: CreditDialogState;

  sourceImageSelectorDialogState: SourceImageSelectorDialogState;

  quickAnimationsDialogState: QuickAnimationsDialogState;

  backgroundMusicDialogState: BackgroundMusicDialogState;
  fontDialogState: FontDialogState;
  editorPanelState: EditorPanelState;
  overlaysTabState: OverlaysTabState;
  voiceoverTabState: VoiceoverTabState;
  videoPreviewState: VideoPreviewState;
  creativeProjectSheetState: CreativeProjectSheetState;

  // Service
  service: EditorService;
};

export function filterTimelineItemsToAnimate(
  timelineItems: CreativeProjectTimelineItem[],
  formItems: Record<
    CreativeProjectTimelineItem["externalId"],
    TimelineItemToAnimate
  >,
) {
  const itemsToAnimate = Object.keys(formItems)
    .filter((formItemId) => {
      const formItem = formItems[formItemId];
      if (!formItem || formItem.promptType === null) return false;
      return timelineItems.some(
        (timelineItem) =>
          timelineItem.externalId === formItemId &&
          timelineItem.startSourceImageExternalId,
      );
    })
    .reduce<
      Record<CreativeProjectTimelineItem["externalId"], TimelineItemToAnimate>
    >((result, formItemId) => {
      result[formItemId] = formItems[formItemId]!;
      return result;
    }, {});

  return itemsToAnimate;
}

export function findElementByExternalIdAndSceneType(
  state: Omit<EditorState, "service">,
  externalId: string,
  sceneType: SceneTypeDto,
) {
  const elements = getElementsByAspectRatio(
    state.video!.compositionJson,
    sceneType,
  );

  if (!elements) {
    return null;
  }

  return elements.find((element) => element.externalId === externalId) ?? null;
}

export function findElementByExternalId(
  state: Omit<EditorState, "service">,
  externalId: string,
) {
  const elements = [
    ...getElementsByAspectRatio(state.video!.compositionJson, "startScene"),
    ...getElementsByAspectRatio(state.video!.compositionJson, "mainScene"),
    ...getElementsByAspectRatio(state.video!.compositionJson, "endScene"),
  ];

  return elements.find((element) => element.externalId === externalId) ?? null;
}

export function getSceneByAspectRatio(
  state: Omit<EditorState, "service">,
  sceneType: "startScene" | "endScene",
) {
  const sceneByAspectRatio = state.video?.compositionJson[sceneType];

  if (!sceneByAspectRatio) {
    return null;
  }

  const aspectRatio = state.video?.compositionJson.aspectRatio;

  if (!aspectRatio) {
    return null;
  }

  return sceneByAspectRatio[aspectRatio] ?? null;
}

export function resolveAnimationProvider(
  animatedImages: Record<string, AnimatedImageDto>,
  timelineAnimatedImagesExternalIds: string[],
  startImageExternalId: string,
  promptType: string,
  endImageExternalId?: string | null,
): AnimatedImageProviderType {
  // Some animations (e.g. timelapses) animate from a start image to a
  // different end image. perspective-2.0 cannot consume an end image, so we
  // must force perspective-3.0 for those prompts regardless of history.
  if (endImageExternalId) {
    return "amplifiles/perspective-3.0";
  }

  const promptExists = timelineAnimatedImagesExternalIds.some(
    (animatedImageExternalId) => {
      const animatedImage = animatedImages[animatedImageExternalId];
      return (
        animatedImage?.promptType === promptType &&
        animatedImage?.startImageExternalId === startImageExternalId
      );
    },
  );

  const { cameraMovement } = parseAnimatedImagePromptType(promptType);
  const prefersPerspective40 =
    cameraMovement === "TRUCK_LEFT" ||
    cameraMovement === "TRUCK_RIGHT" ||
    cameraMovement === "PEDESTAL_UP" ||
    cameraMovement === "PEDESTAL_DOWN";

  if (prefersPerspective40) {
    return promptExists
      ? "amplifiles/perspective-2.1"
      : "amplifiles/perspective-4.0";
  }

  return promptExists
    ? "amplifiles/perspective-4.0"
    : "amplifiles/perspective-2.1";
}

export function resolveEndImageExternalId(
  startImageExternalId: string,
  itemToAnimate: TimelineItemToAnimate,
) {
  if (!itemToAnimate.promptType) {
    return undefined;
  }

  const { style } = parseAnimatedImagePromptType(itemToAnimate.promptType);
  if (style && styleConfigs[style]?.copyStartImageToEndImage) {
    return startImageExternalId;
  }

  return itemToAnimate.endImageExternalId ?? undefined;
}
